import { c as _c } from "react/compiler-runtime";
import type { Address } from '@morpho-org/blue-sdk';
import { InterfaceChainUtils } from '@morpho-org/dapp-core';
import { isAddressEqual } from 'viem';
import { useWrappingGasReserve } from '@/hooks/useWrappingGasReserve';
import { computeGasTokenSpendableBalance } from '@/hooks/wrappingGasReserve.helpers';

/**
 * Gas units reserved when the spent token is also the gas token. These chains charge the fee
 * upfront on the gas limit, not on gas used, and wallets may raise the limit: covers the worst
 * Tempo VaultV2 deposit estimate (1.30M) raised 1.5x by wallets like Rabby (1.95M), with headroom.
 */
const GAS_TOKEN_GAS_LIMIT_UNITS = 2_500_000n;

/**
 * Portion of a token balance a MAX action can spend: on chains whose gas token is also an
 * ERC-20 (Arc's USDC, Tempo's pathUSD) the same balance pays the fee upfront on the gas limit,
 * so a reserve sized for that limit is held back; every other token passes `balance` through unchanged.
 *
 * `spendableBalance` is `undefined` while the reserve loads. An unreadable gas price
 * (`isReserveError`) degrades to the full balance, like `computeGasTokenSpendableBalance`'s
 * unmeasured reserve.
 */
export function useSpendableBalance(t0) {
  const $ = _c(3);
  const {
    chainId,
    token,
    balance,
    enabled
  } = t0;
  const chain = chainId !== undefined && InterfaceChainUtils.isDeployedOnChain.id(chainId) ? InterfaceChainUtils.getMetadata(chainId) : undefined;
  const gasToken = chain?.gasToken;
  const isGasToken = enabled && !!token && !!gasToken && isAddressEqual(token, gasToken.address);
  const {
    reserve,
    isLoading,
    isError
  } = useWrappingGasReserve({
    chainId,
    enabled: isGasToken,
    gasUnits: GAS_TOKEN_GAS_LIMIT_UNITS
  });
  if (!isGasToken || !chain || !gasToken || balance === undefined) {
    return {
      spendableBalance: balance,
      isGasToken,
      isLoading: false,
      isReserveError: false
    };
  }
  if (isLoading) {
    return {
      spendableBalance: undefined,
      isGasToken: true,
      isLoading: true,
      isReserveError: false
    };
  }
  const t1 = computeGasTokenSpendableBalance({
    balance,
    nativeGasReserve: reserve,
    assetDecimals: gasToken.decimals,
    nativeDecimals: chain.chain.nativeCurrency.decimals
  });
  let t2;
  if ($[0] !== isError || $[1] !== t1) {
    t2 = {
      spendableBalance: t1,
      isGasToken: true,
      isLoading: false,
      isReserveError: isError
    };
    $[0] = isError;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}